import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `enhancement:` scope (F2/F3/F4/F17 — the
 * PRO "killer feature" action panels), following the exact `documents:`/
 * `editor:` precedent — NOT a second i18n runtime (E18/§13.3 ban), just
 * physically scoped here because `packages/i18n/**` is outside this task's
 * ownership boundary.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/enhancement.json`.
 */
const en = {
  'pro.badge': 'PRO',
  'upsell.title': 'PRO feature',
  'upsell.upgrade': 'Upgrade to PRO',
  'upsell.maybeLater': 'Maybe later',
  'running.working': 'Working…',
  'running.checkpointed': 'Checkpointed · you can close this and rejoin from the bell.',
  'done.savedAsNewVersion': 'Saved as a new version. The original is untouched.',
  'done.close': 'Done',
  'error.retry': 'Retry',
  'cost.estimating': 'Estimating cost…',
  'cost.credits': '{credits, plural, one {# credit} other {# credits}}',
  'cost.balanceAfter': 'Balance after: {balance}',

  'expand.title': 'Expand',
  'expand.blurb': 'Keep extending this document — add more chapters, indefinitely.',
  'expand.howMuch': 'How much',
  'expand.fewChapters': 'A few chapters (~3)',
  'expand.untilTopic': 'Until a topic is covered',
  'expand.direction': 'Direction (optional)',
  'expand.directionPlaceholder': 'Where should it go next?',
  'expand.cta': 'Expand',
  'expand.success': 'Expanded',
  'expand.doneTitle': 'Expansion complete',

  'cut.title': 'Cut & continue',
  'cut.blurb': 'Truncate at a chosen point and continue generating forward in a new direction.',
  'cut.cutAfterChapter': 'Cut after chapter',
  'cut.direction': 'Direction (optional)',
  'cut.directionPlaceholder': 'Where should it go next?',
  'cut.warning': 'Everything after this point will be regenerated. The original is preserved as a separate version.',
  'cut.cta': 'Cut & continue',
  'cut.confirm.action': 'Cut & continue',
  'cut.success': 'Cut & continued',
  'cut.doneTitle': 'Continuation complete',
  'cut.noChapters': 'This document has no chapters to cut at yet.',

  // mockup-20 (design/app/20-editor.html): rail trigger + modal title read
  // "Elevate difficulty" (not "Change difficulty") — matches the existing
  // `difficulty.cta`/`difficulty.blurb` copy, which already say "Elevate".
  'difficulty.title': 'Elevate difficulty',
  'difficulty.blurb': 'Elevate this document toward mastery, or adjust its complexity.',
  'difficulty.targetLevel': 'Target level',
  'difficulty.currentLevel': 'Currently {level} · consistency-checked across all chapters.',
  'difficulty.alreadyAtLevel': 'Already at this level.',
  'difficulty.cta': 'Elevate',
  'difficulty.success': 'Difficulty changed',
  'difficulty.doneTitle': 'Difficulty updated',
  'difficulty.level.basic': 'Basic',
  'difficulty.level.intermediate': 'Intermediate',
  'difficulty.level.advanced': 'Advanced',
  'difficulty.level.mastery': 'Mastery',

  'translate.title': 'Translate',
  'translate.blurb': 'Produce a parallel translated version in one or more languages.',
  'translate.targetLanguage': 'Target language',
  'translate.noLanguages': 'Choose at least one language.',
  'translate.cta': 'Translate',
  'translate.success': 'Translated',
  'translate.doneTitle': 'Translation complete',

  'toast.jobStarted.action': 'Started',
} as const;

const ro: Record<keyof typeof en, string> = {
  'pro.badge': 'PRO',
  'upsell.title': 'Funcție PRO',
  'upsell.upgrade': 'Treci la PRO',
  'upsell.maybeLater': 'Poate mai târziu',
  'running.working': 'Se procesează…',
  'running.checkpointed': 'Checkpoint salvat · poți închide și reveni din clopoțel.',
  'done.savedAsNewVersion': 'Salvat ca versiune nouă. Originalul rămâne neschimbat.',
  'done.close': 'Gata',
  'error.retry': 'Reîncearcă',
  'cost.estimating': 'Se estimează costul…',
  'cost.credits': '{credits, plural, one {# credit} few {# credite} other {# de credite}}',
  'cost.balanceAfter': 'Sold după: {balance}',

  'expand.title': 'Extinde',
  'expand.blurb': 'Continuă să extinzi acest document — adaugă mai multe capitole, nelimitat.',
  'expand.howMuch': 'Cât de mult',
  'expand.fewChapters': 'Câteva capitole (~3)',
  'expand.untilTopic': 'Până când un subiect e acoperit',
  'expand.direction': 'Direcție (opțional)',
  'expand.directionPlaceholder': 'Încotro ar trebui să meargă mai departe?',
  'expand.cta': 'Extinde',
  'expand.success': 'Extins',
  'expand.doneTitle': 'Extindere finalizată',

  'cut.title': 'Taie și continuă',
  'cut.blurb': 'Trunchiază la un punct ales și continuă generarea într-o direcție nouă.',
  'cut.cutAfterChapter': 'Taie după capitolul',
  'cut.direction': 'Direcție (opțional)',
  'cut.directionPlaceholder': 'Încotro ar trebui să meargă mai departe?',
  'cut.warning': 'Tot ce urmează după acest punct va fi regenerat. Originalul este păstrat ca versiune separată.',
  'cut.cta': 'Taie și continuă',
  'cut.confirm.action': 'Taie și continuă',
  'cut.success': 'Tăiat și continuat',
  'cut.doneTitle': 'Continuare finalizată',
  'cut.noChapters': 'Acest document nu are încă niciun capitol la care să tai.',

  'difficulty.title': 'Ridică dificultatea',
  'difficulty.blurb': 'Ridică acest document spre măiestrie, sau ajustează-i complexitatea.',
  'difficulty.targetLevel': 'Nivel țintă',
  'difficulty.currentLevel': 'Momentan {level} · verificat pentru consistență în toate capitolele.',
  'difficulty.alreadyAtLevel': 'Deja la acest nivel.',
  'difficulty.cta': 'Ridică nivelul',
  'difficulty.success': 'Dificultate schimbată',
  'difficulty.doneTitle': 'Dificultate actualizată',
  'difficulty.level.basic': 'De bază',
  'difficulty.level.intermediate': 'Intermediar',
  'difficulty.level.advanced': 'Avansat',
  'difficulty.level.mastery': 'Măiestrie',

  'translate.title': 'Traduce',
  'translate.blurb': 'Produce o versiune tradusă în paralel, în una sau mai multe limbi.',
  'translate.targetLanguage': 'Limba țintă',
  'translate.noLanguages': 'Alege cel puțin o limbă.',
  'translate.cta': 'Traduce',
  'translate.success': 'Tradus',
  'translate.doneTitle': 'Traducere finalizată',

  'toast.jobStarted.action': 'Pornit',
};

export const ENHANCEMENT_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type EnhancementCatalogKey = keyof typeof en;
